import Period from './Period';
import PeriodRange from './PeriodRange';

const
    NUMBER_NAME_MAP: { [key: string]: string[]; } = {
        '1': ['one', 'yksi'],
        '2': ['two', 'kaksi'],
        '3': ['three', 'kolme'],
        '4': ['four', 'neljä'],
        '5': ['five', 'viisi'],
        '6': ['six', 'kuusi'],
        '7': ['seven', 'seitsemän'],
        '8': ['eight', 'kahdeksan'],
        '9': ['nine', 'yhdeksän'],
        '10': ['ten', 'kymmenen']
    },

    NUMBER_REGEX_PATTERN = `\\d+|${Object.values(NUMBER_NAME_MAP).map(names => names.join('|')).join('|')}`,
    MAX_FILE_NAME_LENGTH = 255;

export function deepClone<T>(value: T): T {
    switch (true) {
        case !value:
            return value;
        case Array.isArray(value):
            return (value as any).map(deepClone) as unknown as T;
        case value instanceof Date:
            return new Date(value as any) as unknown as T;
        case typeof value === 'object':
            var result: T = {} as T;

            for (let i in value) {
                if (Object.prototype.hasOwnProperty.call(value, i)) {
                    result[i] = deepClone(value[i]);
                }
            }

            return result;

        default:
            return value;
    }
};

export type DeepPartial<T> = T | (
    T extends Array<infer P> ? { [index: number]: DeepPartial<P> } | Partial<P>[] :
    T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } :
    T
);
function cloneObjectDescriptors<T>(object: T, asArray: boolean): T {
    const
        result: T = Object.assign(Array.isArray(object) || asArray ? [] : {}, object),
        propertyDescriptors: PropertyDescriptorMap = {};
    if (!Array.isArray(object)) {
        for (let i in result) {
            if (Object.prototype.hasOwnProperty.call(result, i)) {
                const propertyDescriptor = Object.getOwnPropertyDescriptor(object, i);
                if (propertyDescriptor && (propertyDescriptor.get || propertyDescriptor.set)) {
                    propertyDescriptors[i] = propertyDescriptor;
                }
            }
        }
    }

    Object.defineProperties(result, propertyDescriptors);

    return result;
}
/**
 * creates a copy of "original" object and tries to assign recursively all values from "changes" object preserving as much of original references as possible
 * if original is an array, "length" property will also be handled - if "changes" array is shorter, the tail will be deleted from original value
 * if you don't want to update length property, but only some items, "changes" should be object with index-like fields, result will still be an array in this case
 * @param {unknown} original
 * @param {unknown} changes
 */
export function deepAssign<T>(original: T[], changes: { [index: number]: Partial<T> }): T[];
export function deepAssign<T>(original: T, changes: DeepPartial<T>): T;
export function deepAssign<T>(original: T, changes: DeepPartial<T>): T {
    if (original === changes) {
        return original;
    }

    if (typeof original !== 'object' || !original || typeof changes !== 'object' || !changes) {
        return original !== changes ? changes as T : original;
    }

    if (original instanceof Date || changes instanceof Date) {
        return (original as unknown as Period).type !== (changes as unknown as Period).type ||
            (original as unknown as Date).valueOf() !== (changes as unknown as Date).valueOf() ? changes as T : original;
    }

    if (original instanceof PeriodRange || changes instanceof PeriodRange) {
        return (original as unknown as PeriodRange).type !== (changes as unknown as PeriodRange).type ||
            (original as unknown as PeriodRange).length !== (changes as unknown as PeriodRange).length ||
            (original as unknown as PeriodRange).some((_, index) => +(original as unknown as PeriodRange)[index] !== +(changes as unknown as PeriodRange)[index]) ?
            changes as T :
            original;
    }

    // type mismatch should already be detected at build time, but we have plenty of untyped calls yet
    if ((original as any).EntityType && (changes as any).EntityType && (original as any).EntityType !== (changes as any).EntityType) {
        return original !== changes ? changes as T : original;
    }

    let result: T = original;

    if (!Array.isArray(original) && Array.isArray(changes)) {
        result = cloneObjectDescriptors(result, Array.isArray(changes));
    }

    for (let i in (changes as T)) {
        if (Object.prototype.hasOwnProperty.call(changes, i)) {
            const propertyDescriptor = Object.getOwnPropertyDescriptor(original, i);
            if (!propertyDescriptor || !propertyDescriptor.get || propertyDescriptor.set) {
                const propValue = deepAssign(result[i], (changes as T)[i]);
                if (result[i] !== propValue) {
                    if (result === original) {
                        result = cloneObjectDescriptors(result, Array.isArray(changes));
                    }

                    result[i as keyof T] = propValue;
                }
            }
        }
    }
    if (Array.isArray(result) && Array.isArray(changes) && result.length !== changes.length) {
        if (result === original) {
            result = cloneObjectDescriptors(result, Array.isArray(changes));
        }

        (result as any).length = changes.length;
    }

    return result;
};

export function deepFreeze<T>(obj: T): Readonly<T> {
    if (!DEBUG) {
        return obj;
    }

    if (typeof obj === 'object' && obj && !Object.prototype.hasOwnProperty.call(obj, 'IS_FROZEN')) {
        Object.defineProperty(obj, 'IS_FROZEN', {
            configurable: false,
            enumerable: false,
            writable: false,
            value: true
        });

        for (let i in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, i)) {
                deepFreeze(obj[i]);
            }
        }

        Object.freeze(obj);
    }
    return obj;
};

export function deepEqual<T, TKey extends keyof T>(left: T, right: T, ...exceptFields: TKey[]): boolean {
    if (left === right) {
        return true;
    }
    if (typeof left !== typeof right || (left && !right) || (right && !left)) {
        return false;
    }
    if (typeof left !== 'object') {
        return left === right;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }
        for (let i = 0; i < left.length; i++) {
            if (!exceptFields.flatten<TKey>().includes(i as TKey) && !deepEqual(left[i], right[i])) {
                return false;
            }
        }
    } else if (Object.prototype.toString.call(left) === "[object Date]") {
        return (left as unknown as Period).type === (right as unknown as Period).type &&
            (left as unknown as Date).valueOf() === (right as unknown as Date).valueOf();
    }
    else {
        const
            leftKeys: TKey[] = (Object.keys(left) as TKey[]).filter(key => !exceptFields.includes(key)),
            rightKeys: TKey[] = (Object.keys(right) as TKey[]).filter(key => !exceptFields.includes(key));
        if (!deepEqual(leftKeys.sort(), rightKeys.sort())) {
            return false;
        }
        for (let i = 0; i < leftKeys.length; i++) {
            if (!deepEqual(left[leftKeys[i]], right[leftKeys[i]])) {
                return false;
            }
        }
    }
    return true;
};


export function shallowEqual<T, TKey extends keyof T>(left: T, right: T, ...exceptFields: TKey[]): boolean {
    if (left === right) {
        return true;
    }
    if (typeof left !== typeof right || (left && !right) || (right && !left)) {
        return false;
    }
    if (typeof left !== 'object') {
        return left === right;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }
        for (let i = 0; i < left.length; i++) {
            if (!exceptFields.flatten<TKey>().includes(i as TKey) && left[i] !== right[i]) {
                return false;
            }
        }
    } else if (Object.prototype.toString.call(left) === "[object Date]") {
        return (left as unknown as Period).type === (right as unknown as Period).type &&
            (left as unknown as Date).valueOf() === (right as unknown as Date).valueOf();
    }
    else {
        const
            leftKeys: TKey[] = (Object.keys(left) as TKey[]).filter(key => !exceptFields.includes(key)),
            rightKeys: TKey[] = (Object.keys(right) as TKey[]).filter(key => !exceptFields.includes(key));
        if (!shallowEqual(leftKeys.sort(), rightKeys.sort())) {
            return false;
        }
        for (let i = 0; i < leftKeys.length; i++) {
            if (left[leftKeys[i]] !== right[leftKeys[i]]) {
                return false;
            }
        }
    }
    return true;
};

const NODIFF = Symbol('NODIFF');
function _deepDiff<T, TNoDiff>(left: T, right: T, _nodiff: TNoDiff, _hasLeft: boolean = true, _hasRight: boolean = true): Partial<T> | TNoDiff {
    if (left === right && _hasLeft === _hasRight) {
        return _nodiff;
    }
    if (typeof left !== typeof right || (left && !right) || (right && !left)) {
        return right;
    }
    if (typeof left !== 'object') {
        return right;
    }

    let
        keys: (keyof T)[],
        result: Partial<T>,
        hasDiff: boolean = false;
    if (Array.isArray(left) && Array.isArray(right)) {
        keys = (left.length > right.length ? left : right).map((_, index) => index as keyof T);
        result = [] as unknown as Partial<T>;
    }
    else {
        keys = [...(Object.keys(left) as (keyof T)[]), ...(Object.keys(right) as (keyof T)[])].unique();
        result = {} as unknown as Partial<T>;

    }
    for (let i = 0; i < keys.length; i++) {
        const propDiff: Partial<T[keyof T]> | typeof NODIFF = _deepDiff<T[keyof T], typeof NODIFF>(
            left[keys[i]],
            right[keys[i]],
            NODIFF,
            Object.prototype.hasOwnProperty.call(left, keys[i]),
            Object.prototype.hasOwnProperty.call(right, keys[i])
        );
        if (propDiff !== NODIFF) {
            result[keys[i]] = propDiff as T[keyof T];
            hasDiff = true;
        }
    }
    return hasDiff ? result : _nodiff;
}

export const deepDiff = <T>(left: T, right: T): Partial<T> | undefined => _deepDiff(left, right, undefined);

export function pick<T, TKey extends keyof T>(obj: T, ...keys: TKey[]): Pick<T, TKey> {
    const result: Partial<T> = {};
    keys.flatten<TKey>().forEach(key => result[key] = obj[key]);
    return result as Pick<T, TKey>;
};

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export function omit<T, TKey extends keyof T>(obj: T, ...keys: TKey[]): Omit<T, TKey> {
    const result: Partial<T> = { ...obj };
    keys.flatten<TKey>().forEach(key => delete result[key]);
    return result as Omit<T, TKey>;
};

export const toArray = <T>(v: T | T[]): T[] => Array.isArray(v) ? v : [v];



export const normalizeURI = (uri: string): string => uri.indexOf('http') !== 0 ? 'http://' + uri : uri;
export const stripURI = (uri: string): string => uri.replace(/^https?:\/\/|[\?\&\#].*$/g, '');
export const stripDomain = (uri: string): string => uri.replace(/^https?:\/\/|[\/\?\&\#].*$/g, '');

export const isValidURI = (text: string): boolean => {
    let url;

    try {
        url = new URL(text);
    }
    catch (_) {
        return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:';
};

export function addEvent<T extends keyof WindowEventMap>(element: Window, type: T, callback: (this: Window, ev: WindowEventMap[T]) => unknown): void;
export function addEvent<T extends keyof DocumentEventMap>(element: Document, type: T, callback: (this: Document, ev: DocumentEventMap[T]) => unknown): void;
export function addEvent<T extends keyof HTMLElementEventMap>(element: HTMLElement, type: T, callback: (this: HTMLElement, ev: HTMLElementEventMap[T]) => unknown, capture?: boolean): void;
export function addEvent(element: any, type: any, callback: any, capture: boolean = false) {
    if (!element) return;
    if (element.addEventListener) {
        element.addEventListener(type, callback, { passive: false, capture });
    } else if ((element as any).attachEvent) {
        element.attachEvent('on' + type, callback, { passive: false, capture });
    } else {
        element['on' + type] = callback;
    }
};


export function removeEvent<T extends keyof WindowEventMap>(element: Window, type: T, callback: (this: Window, ev: WindowEventMap[T]) => unknown): void;
export function removeEvent<T extends keyof DocumentEventMap>(element: Document, type: T, callback: (this: Document, ev: DocumentEventMap[T]) => unknown): void;
export function removeEvent<T extends keyof HTMLElementEventMap>(element: HTMLElement, type: T, callback: (this: HTMLElement, ev: HTMLElementEventMap[T]) => unknown, capture?: boolean): void;
export function removeEvent(element: any, type: any, callback: any, capture: boolean = false) {
    if (!element) return;
    if (element.removeEventListener) {
        element.removeEventListener(type, callback, { capture });
    } else if ((element as any).detachEvent) {
        (element as any).detachEvent('on' + type, { capture });
    } else {
        (element as any)['on' + type] = null;
    }
};

export function triggerEvent(element: Window, type: keyof WindowEventMap): void;
export function triggerEvent(element: HTMLElement, type: keyof HTMLElementEventMap): void;
export function triggerEvent(element: any, type: any) {
    if (!element) return;
    let event;
    if (document.createEvent) {
        event = document.createEvent('HTMLEvents');
        event.initEvent(type, true, true);
    } else if ((document as any).createEventObject) {
        event = (document as any).createEventObject();
        event.eventType = type;
    }
    event.eventName = type;
    if (element.dispatchEvent) {
        element.dispatchEvent(event);
    } else if ((element as any).fireEvent) {
        (element as any).fireEvent('on' + event.eventType, event);
    } else if ((element as any)[type]) {
        (element as any)[type]();
    } else if ((element as any)['on' + type]) {
        (element as any)['on' + type]();
    }
};

export function isAncestorOrSelf(ancestorOrSelf: Element, self: Element): boolean {
    let current: Node | null = self;
    while (current && current !== ancestorOrSelf) {
        current = current.parentNode;
    }
    return current === ancestorOrSelf;
};

export const isOverEvent = (event: MouseEvent, node: Element): boolean =>
    !isAncestorOrSelf(node, (event as any).fromElement || event.relatedTarget) && isAncestorOrSelf(node, (event as any).toElement || event.target);
export const isOutEvent = (event: MouseEvent, node: Element): boolean =>
    isAncestorOrSelf(node, (event as any).fromElement || event.target) && !isAncestorOrSelf(node, (event as any).toElement || event.relatedTarget);


const
    SHORT_STRINT_SUFFIXES: string[] = ['a', 'f', 'p', 'n', 'μ', 'm', '', 'K', 'M', 'G', 'T', 'P', 'E'],
    NO_SUFFIX_INDEX: number = SHORT_STRINT_SUFFIXES.indexOf('');
/**
 * @deprecated Use FormattedNumber
 */
export function toShortString(number: number): string {
    if (!number) {
        return '0';
    }

    const
        abs: number = Math.abs(number),
        minPossiblePower: number = -NO_SUFFIX_INDEX,
        maxPossiblePower: number = SHORT_STRINT_SUFFIXES.length - 1 - NO_SUFFIX_INDEX;
    let closestPower: number = Math.min(Math.max(minPossiblePower, Math.floor(Math.log10(abs) / 3)), maxPossiblePower);
    if ((closestPower < 0 || Math.round(abs / Math.pow(10, closestPower * 3)) >= 1000) && closestPower < maxPossiblePower) {
        closestPower++;
    }
    if (!closestPower) {
        return number > 1 ? number.toFixed(0) : roundTo(number, 3).toString();
    }
    else {
        const depowered = abs / Math.pow(10, closestPower * 3);
        return (number >= 0 ? '' : '-') + (depowered > 1 ? Math.round(depowered) : closestPower > minPossiblePower + 1 ? roundTo(depowered, 3) : depowered) + ' ' + SHORT_STRINT_SUFFIXES[NO_SUFFIX_INDEX + closestPower];
    }
};

/**
 * @deprecated Use FormattedNumber
 */
export const roundTo = (value: number, places: number): number => {
    // https://stackoverflow.com/a/21323330
    if (places === 0) { return Math.round(value); }
    else if (isNaN(value) || !(places % 1 === 0)) {
        return NaN;
    }

    let parts = value.toString().split('e');
    value = Math.round(+`${parts[0]}e${parts[1] ? +parts[1] + places : places}`);

    parts = value.toString().split('e');
    return +`${parts[0]}e${parts[1] ? +parts[1] - places : -places}`;
};

export function exactNumbers(value: string): number[] {
    if (!value || !value.length) {
        return [];
    }

    const matches = value.match(new RegExp(NUMBER_REGEX_PATTERN, 'gi'));

    if (!matches || !matches.length) {
        return [];
    }

    return [...matches]
        .map(m => Number.isInteger(+m) ? m : Object.keys(NUMBER_NAME_MAP).find(key => NUMBER_NAME_MAP[key].includes(`${m}`)))
        .map(m => Number(m))
        .filter(m => Number.isInteger(m));
}

export const absoluteCompare = (reference: number, value: number): number => value - reference;
export const percentageCompare = (reference: number, value: number): number => {
    if (!reference) { return Math.sign(value) * 100; }

    const
        difference = (value - reference),
        absoluteDifference = Math.abs(difference / reference) * 100;

    return absoluteCompare(reference, value) < 0 ? -absoluteDifference : absoluteDifference;
};
export const portionCompare = (reference: number, value: number): number => (reference ? value / reference : Math.sign(value)) * 100;
export const exactValueCompare = (_reference: number, value: number): number => value;
export const exactReferenceCompare = (reference: number, _value: number): number => reference;
export const divisionCompare = (reference: number, value: number): number => reference ? value / reference : 0;


export function colorHash<T>(obj: T): string {
    const string = JSON.stringify(obj).slice(0, 100);

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xFF;

        if (value < 80) {
            value = value + 80;
        }

        // Adjust contrast for white font color. https://www.w3.org/WAI/ER/WD-AERT/#color-contrast
        // Adjust green color:
        if (i === 1 && value > 127) {
            value = 127;
        }
        // Adjust other colors:
        if (value > 170) {
            value = 170;
        }

        color += ('00' + value.toString(16)).slice(-2);
    }

    return color;
}

export function assertNever(_: any, message?: string): never { throw new Error(message || 'Unexpected object.'); }

export function download(url: string, filename: string) {
    const isDataURL = url.startsWith('data:');

    const msSaveBlobAvailable = 'msSaveBlob' in navigator;
    const objectURLAvailable = !!(window.URL && window.URL.createObjectURL);

    if (isDataURL && (msSaveBlobAvailable || objectURLAvailable)) {
        // copied from http://danml.com/download.html
        var parts = url.split(/[:;,]/),
            type = parts[1],
            decoder = parts[2] == "base64" ? atob : decodeURIComponent,
            binData = decoder(parts.pop()!),
            mx = binData.length,
            i = 0,
            uiArr = new Uint8Array(mx);

        for (i; i < mx; ++i) uiArr[i] = binData.charCodeAt(i);

        const blob = new Blob([uiArr], { type: type });

        if (msSaveBlobAvailable) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const objectURL = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.setAttribute('href', objectURL);
            link.setAttribute('download', filename);
            link.click();

            URL.revokeObjectURL(objectURL);
        }
    }
    else {
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.click();
    }
}


/**
 * 
 * @param color Hex color (including the #)
 * @param percent Positive lightens and negative darkens the color
 */
export function shadeHexColor(color: string, percent: number): string {
    if (!percent) return color;

    const
        f = parseInt(color.slice(1), 16),
        t = percent < 0 ? 0 : 255,
        p = percent < 0 ? percent * -1 : percent,
        R = f >> 16,
        G = f >> 8 & 0x00FF,
        B = f & 0x0000FF;

    return `#${(0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1)}`;
}

/**
 * 
 * @param color RGB color. Doesn't support the alpha channel.
 * @param percent Positive lightens and negative darkens the color
 */
export function shadeRGBColor(color: string, percent: number): string {
    if (!percent) return color;

    const
        f = color.split(','),
        t = percent < 0 ? 0 : 255,
        p = percent < 0 ? percent * -1 : percent,
        R = parseInt(f[0].slice(4)),
        G = parseInt(f[1]),
        B = parseInt(f[2]);

    return `rgb(${Math.round((t - R) * p) + R},${Math.round((t - G) * p) + G},${Math.round((t - B) * p) + B})`;
}

/**
 * Tries to guess if the color is given in the hex or RGB and uses the shadeHexColor / shadeRGBColor accordingly
 * @param color Hex or RGB color
 * @param percent Positive lightens and negative darkens the color
 */
export function shadeColor(color: string, percent: number): string {
    if (!percent) return color;
    if (color.length > 7) { return shadeRGBColor(color, percent / 100); }
    else { return shadeHexColor(color, percent / 100); }
}

export function hexToRGBA(hex: string): string {
    let rgba: string | string[] = '';

    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        rgba = hex.substring(1).split('');

        if (rgba.length == 3) {
            rgba = [rgba[0], rgba[0], rgba[1], rgba[1], rgba[2], rgba[2]];
        }

        const hexadecimal: any = '0x' + rgba.join('');
        return 'rgba(' + [(hexadecimal >> 16) & 255, (hexadecimal >> 8) & 255, hexadecimal & 255].join(',') + ',1)';
    }

    throw new Error('Bad Hex');
}

/**
 * Tries to guess if the color is given in the hex or RGB and uses the shadeHexColor / shadeRGBColor accordingly
 * @param color Hex or RGB color
 * @param alpha Decimal that's will be used as the rgba color's alpha
 */
export function setColorAlpha(color: string, alpha: number): string {
    let rgbColor = color;

    if (color.length <= 7) {
        rgbColor = hexToRGBA(color);
    }

    const
        parts = rgbColor.replace(/[^0-9,]/g, '').split(',').map(part => +part),
        R = parts[0],
        G = parts[1],
        B = parts[2];

    return `rgba(${R}, ${G}, ${B}, ${alpha})`;
}

/**
 * Tries to shade a foreground color (like font color) so that it is visible against the background
 * @param backgroundColor Hex or RGB color
 * @param darkColor The darker color that will be used if the background color is determined to be light
 * @param lightColor The lighter color that will be used if the background color is determined to be dark
 */
export function pickColorAgainstBackground(backgroundColor: string, darkColor: string, lightColor: string): string {
    const
        rgbaColor = hexToRGBA(backgroundColor).replace(/\s/g, ''),
        rgb: number[] = (rgbaColor.match(/^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3})/) || []).slice(1, 4).map(v => +v).filter(v => typeof v === 'number');

    if (rgb.length === 3) {
        // Original source https://stackoverflow.com/a/11868159
        const brightness = Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000);
        if (brightness <= 125) { return lightColor; }
    }

    return darkColor;
}

/** 
 * Returns a single rgb color interpolation between given rgb color
 * @param color1 the numeric values of rgb color
 * @param color2 the numeric values of rgb color
 * @param factor the decimal percentage on how much the color should be changed
 */
export function interpolateColor(color1: number[], color2: number[], factor: number) {
    if (arguments.length < 3) { factor = 0.5; }

    let result = color1.slice();

    for (let i = 0; i < 3; i++) {
        result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
    }

    return `rgb(${result.slice(0, 3)})`;
};

/** 
 * Creates a gradually changing array of rgb colors between provided colors. Expects that the provided colors are in rgb format.
 * Source: https://graphicdesign.stackexchange.com/a/83867
 * @param color1 the starting color.
 * @param color2 the target color.
 * @param steps the number of colors expected.
 */
export function interpolateColors(color1: string, color2: string, steps: number) {
    const
        stepFactor = 1 / Math.max(1, steps - 1),
        interpolatedColorArray = [],

        color1Parts = color1.match(/\d+/g)!.map(Number),
        color2Parts = color2.match(/\d+/g)!.map(Number);

    for (let i = 0; i < steps; i++) {
        interpolatedColorArray.push(interpolateColor(color1Parts, color2Parts, stepFactor * i));
    }

    return interpolatedColorArray;
};

/**
 * @returns Returns the current date in DDMMYYYY format
 */
export function getDateTimestamp(): string {
    const
        date = new Date(),
        day = date.getUTCDate(),
        month = date.getUTCMonth() + 1,
        year = date.getUTCFullYear();

    let timestamp = '';

    if (day < 10) timestamp += "0";
    timestamp += day.toString();

    if (month < 10) timestamp += "0";
    timestamp += month.toString() + year.toString();

    return timestamp;
}

/**
 * Whitespace characters are replaced with underscores and the string is converted to lowercase
 * @param fileName
 * @returns A sanitized string
 */
export function sanitizeFileName(fileName: string): string {
    const
        extensionSeparatorIndex = fileName.lastIndexOf('.'),
        name = fileName.substring(0, extensionSeparatorIndex >= 0 ? extensionSeparatorIndex : undefined),
        extension = extensionSeparatorIndex >= 0 ? fileName.substring(extensionSeparatorIndex + 1) : '';

    let sanitizedName = name.replace(/\s/g, '_').replace(/[^a-zäöå0-9_]/gi, '').toLowerCase();

    if (sanitizedName.length > MAX_FILE_NAME_LENGTH) {
        sanitizedName = sanitizedName.substring(0, MAX_FILE_NAME_LENGTH);
    }

    return sanitizedName + (extension ? '.' + extension : '');
}